import React from 'react'

import * as S from './WorkspaceSidebarNoticeCanceledSubscription.styles'
import { WorkspaceNoticeComponentProps } from '../types'
import {
  BillingWorkspaceNoticeFragment,
  WorkspaceMinimalFragment,
  useGetBillingForWorkspaceNoticeQuery,
} from '@sketch/gql-types'
import { routes } from '@sketch/modules-common'

import { ReactComponent as LicenseIconMonochrome } from '@sketch/icons/license-diamond-monochrome-24'
import { ReactComponent as ExclamationMarkTriangleMonochromeIcon } from '@sketch/icons/exclamation-mark-triangle-monochrome-24'

import { WorkspaceSidebarNoticeContainer } from '../WorkspaceSidebarNoticeContainer'
import {
  NoticeContentTemplateWithOptionalActionAndLearnMore,
  NoticeContentTemplateWithOptionalActionAndLearnMoreProps,
} from '../NoticeContentTemplates'
import { dateFormat } from '@sketch/utils'
import {
  canManageSubscription,
  isOrWillBeCanceledSubscription,
  isWillBeCanceledSubscription,
} from '../workspaceStatusUtils'
import { SvgComponent } from '@sketch/icons'

type WorkspaceSidebarNoticeCanceledSubscriptionProps = WorkspaceNoticeComponentProps

export function WorkspaceSidebarNoticeCanceledSubscription({
  workspace,
}: WorkspaceSidebarNoticeCanceledSubscriptionProps) {
  const billingQuery = useGetBillingForWorkspaceNoticeQuery({
    variables: { identifier: workspace.identifier },
  })
  const billing = billingQuery.data?.workspace.customer?.billing

  if (billingQuery.loading || billingQuery.error || !billing) {
    return null
  }

  if (!isOrWillBeCanceledSubscription(billing)) {
    // We are not expecting to end up in that state.
    return null
  }

  const noticeContentData = getNoticeContentData(billing, workspace)

  return (
    <WorkspaceSidebarNoticeContainer>
      <NoticeContentTemplateWithOptionalActionAndLearnMore
        action={noticeContentData.action}
        icon={noticeContentData.icon}
      >
        <S.ContentContainer>
          <span>{noticeContentData.title}</span>
          {noticeContentData.details && (
            <S.DetailsParagraph>
              <S.FontWeightRegular>
                {noticeContentData.details}
              </S.FontWeightRegular>
            </S.DetailsParagraph>
          )}
        </S.ContentContainer>
      </NoticeContentTemplateWithOptionalActionAndLearnMore>
    </WorkspaceSidebarNoticeContainer>
  )
}

type TemplateButtonAction = NoticeContentTemplateWithOptionalActionAndLearnMoreProps['action']

type NoticeContentData = {
  title: string
  icon: SvgComponent
  details: string | null
  action?: TemplateButtonAction
}

/** Get different notice content based on reason of cancelation */
function getNoticeContentData(
  billing: BillingWorkspaceNoticeFragment,
  workspace: WorkspaceMinimalFragment
): NoticeContentData {
  const billingSettingRoute = routes.WORKSPACE_SETTINGS_BILLING.create({
    workspaceId: workspace.identifier,
  })
  const subscribeRoute = routes.WORKSPACE_SUBSCRIBE.create({
    workspaceId: workspace.identifier,
  })

  const hasRoleToPerformAction = canManageSubscription(workspace.userRole)

  const updatePaymentDetailsAction:
    | TemplateButtonAction
    | undefined = hasRoleToPerformAction
    ? {
        label: 'Review Payment Details',
        link: { to: billingSettingRoute },
      }
    : undefined

  const resubscribeAction:
    | TemplateButtonAction
    | undefined = hasRoleToPerformAction
    ? {
        label: 'Subscribe Again',
        link: { to: subscribeRoute },
      }
    : undefined

  if (isWillBeCanceledSubscription(billing)) {
    return {
      title: 'Subscription canceled',
      icon: LicenseIconMonochrome,
      details: `You can keep using Sketch until ${dateFormat(
        new Date(billing.subscriptionEnd!)
      )}. Come back soon!`,
    }
  }

  if (billing.status === 'PAST_DUE') {
    return {
      title: 'Your payment is due',
      icon: ExclamationMarkTriangleMonochromeIcon,
      details: hasRoleToPerformAction
        ? 'Please check your billing details or contact customer support'
        : 'Please ask your Workspace admin to check your billing details',
      action: updatePaymentDetailsAction,
    }
  }

  if (billing.status === 'UNPAID' || billing.status === 'INCOMPLETE_EXPIRED') {
    return {
      title: 'Subscription canceled',
      icon: ExclamationMarkTriangleMonochromeIcon,
      details: 'Something went wrong with a recent payment',
      action: updatePaymentDetailsAction,
    }
  }

  if (billing.status === 'INCOMPLETE') {
    return {
      title: 'We were unable to validate your card',
      icon: ExclamationMarkTriangleMonochromeIcon,
      details: null,
      action: updatePaymentDetailsAction,
    }
  }

  return {
    title: `Your subscription expired on ${dateFormat(
      new Date(billing.subscriptionEnd!)
    )}`,
    icon: LicenseIconMonochrome,
    details: null,
    action: resubscribeAction,
  }
}
